import { FormControl, FormHelperText, TextField, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect } from "react";
import { ManagerService } from "../../../../services/manager-service";
import { useToast } from "../../../../context/toast-context";
import { ManagerResponseDto } from "../../../../services/dto/response/manager-response-dto";
import { ButtonForm } from "../../button-form";
import { BranchService } from "../../../../services/branch-service";
import { Branch } from "../../../../types";
import { LocationSelectors } from "../../../LocationSelectors";
import { DOCUMENT_LENGTHS } from "../../../../constants/validations";

interface AddManagerFormARProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onAddManager?: (newManager: ManagerResponseDto) => void;
  fetchManagers?: () => Promise<void>;
}

/**
 * Formulario de creación de manager para Argentina
 * Incluye campos específicos de AR: CUIL, Provincia y Ciudad
 */
export function AddManagerFormAR({
  setIsModalOpen,
  onAddManager,
  fetchManagers,
}: AddManagerFormARProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const managerService = useMemo(() => new ManagerService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const { showToast } = useToast();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    identityNumber: "",
    address: "",
    phone: "",
    departmentId: "",
    cityId: "",
    branchesIds: "",
  });

  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    IdentityType: "CUIL",
    IdentityNumber: "",
    Address: "",
    Phone: "",
    DepartmentId: 0,
    CityId: 0,
    BranchesIds: [] as number[],
  });

  useEffect(() => {
    const loadBranches = async () => {
      try {
        await branchService.getBranches(
          { limit: 100, offset: 0 },
          (data) => {
            setBranches(data.branches);
          },
          (errorMessage) => {
            showToast(errorMessage, "error");
          }
        );
      } catch (error) {
        showToast(t("common.errors.generic"), "error");
      }
    };

    loadBranches();
  }, [branchService, showToast, t]);

  const validate = () => {
    let isValid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      identityNumber: "",
      address: "",
      phone: "",
      departmentId: "",
      cityId: "",
      branchesIds: "",
    };

    if (!formData.FirstName) {
      newErrors.firstName = t("managers.create.errors.name");
      isValid = false;
    }

    if (!formData.LastName) {
      newErrors.lastName = t("managers.create.errors.lastName");
      isValid = false;
    }

    if (!formData.Email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.email = t("managers.create.errors.email");
      isValid = false;
    }

    if (!formData.IdentityNumber) {
      newErrors.identityNumber = t("managers.create.errors.identityNumber");
      isValid = false;
    } else if (formData.IdentityNumber.length !== DOCUMENT_LENGTHS.AR_CUIL_LENGTH) {
      newErrors.identityNumber = t("managers.create.errors.identityNumber-min");
      isValid = false;
    } else if (!formData.IdentityNumber.startsWith("20") && !formData.IdentityNumber.startsWith("23") && !formData.IdentityNumber.startsWith("24") && !formData.IdentityNumber.startsWith("27")) {
      newErrors.identityNumber = t("managers.create.errors.identityNumber-note");
      isValid = false;
    }

    if (formData.BranchesIds.length === 0) {
      newErrors.branchesIds = t("managers.create.errors.branchesIds");
      isValid = false;
    }

    if (!formData.DepartmentId) {
      newErrors.departmentId = t("managers.create.errors.departmentId");
      isValid = false;
    }

    if (!formData.CityId) {
      newErrors.cityId = t("managers.create.errors.cityId");
      isValid = false;
    }

    if (!formData.Address) {
      newErrors.address = t("managers.create.errors.address");
      isValid = false;
    } else if (formData.Address.length < 5) {
      newErrors.address = t("managers.create.errors.address-min");
      isValid = false;
    } else if (formData.Address.length > 50) {
      newErrors.address = t("managers.create.errors.address-max");
      isValid = false;
    }

    if (!formData.Phone) {
      newErrors.phone = t("managers.create.errors.phone-required");
      isValid = false;
    } else if (formData.Phone.length < 7 || formData.Phone.length > 20) {
      newErrors.phone = t("managers.create.errors.phone-size");
      isValid = false;
    } 

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      await managerService.createManager(
        {
          ...formData,
          DepartmentId: formData.DepartmentId.toString(),
          CityId: formData.CityId.toString(),
          CountryCode: "AR",
        },
        async (data) => {
          setIsModalOpen && setIsModalOpen(false);
          showToast(t("managers.create.success"), "success");
          if (fetchManagers) {
            await fetchManagers();
          }
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
    const selectedOptions = event.target.value as number[];
    setSelectedBranches(selectedOptions);
    setFormData((prev) => ({ ...prev, BranchesIds: selectedOptions }));
  };

  const textFieldStyles = {
    "& .MuiInput-root": {
      color: "#000",
      fontFamily: "Montserrat",
      "&:before": {
        borderBottom: "1px solid #838383",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#898989",
      },
    },
    "& label.Mui-focused": {
      color: "#7e7e7e",
    },
    "& .MuiInputLabel-standard": {
      color: "#000",
      fontWeight: "400",
      fontFamily: "Montserrat",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#838383",
    },
  };

  return (
    <form onSubmit={handleSubmit} className="">
      <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
        <h1>{t("managers.create-title")}</h1>
      </div>
      <div className="grid gap-4 p-6">
        <TextField
          type="text"
          name="FirstName"
          error={!!errors.firstName}
          helperText={errors.firstName}
          label={t("managers.create.name")}
          value={formData.FirstName}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="LastName"
          error={!!errors.lastName}
          helperText={errors.lastName}
          label={t("managers.create.lastName")}
          value={formData.LastName}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="email"
          name="Email"
          error={!!errors.email}
          helperText={errors.email}
          label={t("managers.create.email")}
          value={formData.Email}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="IdentityNumber"
          error={!!errors.identityNumber}
          helperText={errors.identityNumber}
          label={"CUIL"}
          value={formData.IdentityNumber}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="Address"
          error={!!errors.address}
          helperText={errors.address}
          label={t("managers.create.address")}
          value={formData.Address}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="Phone"
          error={!!errors.phone}
          helperText={errors.phone}
          label={t("managers.create.phone")}
          value={formData.Phone}
          onChange={handleChange}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            let value = input.value;
            value = value.replace(/[^\d]/g, "");
            if (value.length > 10) {
              value = value.substring(0, 10);
            }
            input.value = value;
            setFormData(prev => ({ ...prev, Phone: value }));
          }}
          variant="standard"
          sx={textFieldStyles}
        />

        <LocationSelectors
          onProvinceChange={(provinceId) => {
            setFormData(prev => ({
              ...prev,
              DepartmentId: parseInt(provinceId),
              CityId: 0
            }));
            setErrors(prev => ({ ...prev, departmentId: "" }));
          }}
          onCityChange={(cityId) => {
            setFormData(prev => ({
              ...prev,
              CityId: parseInt(cityId)
            }));
            setErrors(prev => ({ ...prev, cityId: "" }));
          }}
        />
        {(errors.departmentId || errors.cityId) && (
          <p className="text-red text-xs mt-1">
            {errors.departmentId || errors.cityId}
          </p>
        )}

        <FormControl error={!!errors.branchesIds} variant="standard" fullWidth>
          <Select
            sx={{
              paddingX: 2,
              paddingY: 1,
              margin: 0,
              fontStyle: "normal",
              fontFamily: "Montserrat",
              fontSize: "1rem",
              height: "100%",
              width: "100%",
              borderRadius: "9999px",
              border: "1px solid",
              borderColor: "#838383",
              backgroundColor: "transparent",
              outline: "none",
              "&:focus": {
                outlineColor: "primary.main",
              },
              "& .MuiSelect-select": {
                padding: 0,
                display: "block",
                minWidth: 0,
              },
              "&.MuiInputBase-root.MuiOutlinedInput-root": {
                padding: "4px 8px",
              },
              "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                {
                  WebkitTextFillColor: "#838383",
                  opacity: "70%",
                },
              "& .MuiSvgIcon-root.MuiSelect-icon": {
                color: "white",
              },
              color: "#000",
              fontWeight: "400",
              "&:before": {
                borderBottom: "none"
              },
              "&:after": {
                borderBottom: "none"
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "none"
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "white",
                  color: "#000",
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "#f5f5f5"
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#e0e0e0",
                      "&:hover": {
                        backgroundColor: "#d5d5d5"
                      }
                    }
                  }
                },
              },
            }}
            multiple
            name="BranchesIds"
            value={selectedBranches}
            onChange={handleBranchChange}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <span className="block truncate">
                    {t("managers.branchesIds")}
                  </span>
                );
              }
              return selected
                .map((id) => branches.find((b) => b.branchId === id)?.name)
                .join(", ");
            }}
          >
            <MenuItem
              sx={{
                fontFamily: "Montserrat",
              }}
              disabled
              value=""
            >
              <span>{t("managers.branchesIds")}</span>              
            </MenuItem>
            {branches.map((branch) => (
              <MenuItem
                sx={{
                  fontFamily: "Montserrat",
                }}
                key={branch.branchId}
                value={branch.branchId}
              >
                {branch.name}
              </MenuItem>
            ))}
          </Select>
          {errors.branchesIds && (
            <FormHelperText>{errors.branchesIds}</FormHelperText>
          )}
        </FormControl>

        <div className="w-full flex flex-col gap-4">
          <ButtonForm loading={loading}>
            {t("managers.create.submit")}
          </ButtonForm>
        </div>
      </div>
    </form>
  );
}

